import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Landmark, PenTool, Cog, Hammer, Sparkles, BarChart3, TrendingUp, Building2, MapPin, ArrowRight, Play } from "lucide-react";
import Seo from "../components/common/Seo";
import { ArrowIcon } from "../components/common/Section";
import { useI18n } from "../lib/i18n";
import LeadCallout from "../components/common/LeadCallout";

/* ============================================================================
   1. HERO — Full-screen cinematic
============================================================================ */
const HeroChapter = () => {
  const { pick } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden bg-brand-navy" data-testid="about-hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <video autoPlay muted loop playsInline poster="https://images.unsplash.com/photo-1481026469463-66327c86e544" className="w-full h-full object-cover opacity-60">
          <source src="https://cdn.pixabay.com/video/2023/10/03/183366-870018235_large.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/60 to-brand-navy" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col items-center justify-center container-x text-center text-white">
        <motion.div
          initial={{ opacity: 0, letterSpacing: "0.5em" }}
          animate={{ opacity: 1, letterSpacing: "0.3em" }}
          transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
          className="text-[0.72rem] uppercase text-brand-gold-bright"
        >
          {pick({ tr: "Hakkımızda — Bölüm 01", en: "About — Chapter 01", ar: "من نحن — الفصل 01" })}
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
          className="font-serif text-white mt-8 leading-[0.95]"
          style={{ fontSize: "clamp(4rem, 12vw, 12rem)" }}
        >
          {pick({ tr: "Biz Kimiz?", en: "Who We Are", ar: "من نحن" })}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-10 max-w-2xl text-white/75 text-lg md:text-xl leading-relaxed"
        >
          {pick({
            tr: "Bir yatırımın hikâyesi bir fikirle başlar. Bizim işimiz o fikri kalıcı bir esere dönüştürmektir.",
            en: "The story of an investment begins with an idea. Our craft is turning that idea into an enduring landmark.",
            ar: "قصة الاستثمار تبدأ بفكرة. مهمتنا هي تحويل تلك الفكرة إلى إنجاز دائم.",
          })}
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="absolute bottom-10 flex flex-col items-center gap-3 text-white/50">
          <span className="text-[0.65rem] tracking-[0.3em] uppercase">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-px h-12 bg-gradient-to-b from-brand-gold-bright to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ============================================================================
   2. CIRCULAR WORKFLOW — 6-stage process
============================================================================ */
const WORKFLOW = [
  { icon: Landmark, tr: "Arsa Tedariği", en: "Land Acquisition", ar: "شراء الأرض", d: { tr: "Doğru konum, doğru zamanlama, doğru fiyat.", en: "Right location, right timing, right price.", ar: "الموقع المناسب في الوقت المناسب بالسعر المناسب." } },
  { icon: PenTool, tr: "Mimari Tasarım", en: "Architectural Design", ar: "التصميم المعماري", d: { tr: "Prestijli ve fonksiyonel mimarlık.", en: "Prestigious and functional architecture.", ar: "عمارة فخمة وعملية." } },
  { icon: Cog, tr: "Mühendislik", en: "Engineering", ar: "الهندسة", d: { tr: "Statik, mekanik, elektrik — her disiplin.", en: "Structural, MEP — every discipline.", ar: "الإنشائية والميكانيكية والكهربائية." } },
  { icon: Hammer, tr: "İnşaat", en: "Construction", ar: "البناء", d: { tr: "Zamanında, bütçede, kalitede.", en: "On time, on budget, on quality.", ar: "في الوقت، ضمن الميزانية، بالجودة." } },
  { icon: Sparkles, tr: "Franchise / Marka", en: "Franchise / Brand", ar: "الامتياز / العلامة", d: { tr: "Uluslararası marka lansmanı ve büyüme.", en: "International brand launch and growth.", ar: "إطلاق العلامة والنمو." } },
  { icon: BarChart3, tr: "İşletme Yönetimi", en: "Operations", ar: "إدارة العمليات", d: { tr: "Yatırımınızı kâra dönüştürüyoruz.", en: "We turn your investment into profit.", ar: "نحول الاستثمار إلى ربح." } },
];

const WorkflowChapter = () => {
  const { pick, lang } = useI18n();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="bg-white relative overflow-hidden" data-testid="about-workflow">
      <div className="container-x py-24 md:py-36">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.8 }} className="text-[0.7rem] tracking-[0.3em] uppercase text-brand-gold-bright">
            {pick({ tr: "Bölüm 02 — Süreç", en: "Chapter 02 — The Process", ar: "الفصل 02 — العملية" })}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif mt-8 text-brand-navy leading-[1.05]"
            style={{ fontSize: "clamp(2.5rem, 5vw, 5rem)" }}
          >
            {pick({
              tr: "Bir yatırımın tüm süreçlerini tek çatı altında yönetiyoruz.",
              en: "We manage every step of an investment under one roof.",
              ar: "ندير كل مراحل الاستثمار تحت سقف واحد.",
            })}
          </motion.h2>
        </div>

        {/* Workflow items - vertical timeline with connecting line */}
        <div className="mt-20 md:mt-28 relative max-w-4xl mx-auto">
          {/* connecting spine */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-brand-gold-bright via-brand-primary to-transparent md:-translate-x-1/2" />
          <div className="space-y-16 md:space-y-24">
            {WORKFLOW.map((w, i) => {
              const Icon = w.icon;
              const left = i % 2 === 0;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, delay: 0.05, ease: [0.19, 1, 0.22, 1] }}
                  className={`relative flex flex-col md:flex-row items-start gap-6 md:gap-10 ${left ? "" : "md:flex-row-reverse"}`}
                  data-testid={`workflow-step-${i}`}
                >
                  {/* node */}
                  <div className={`absolute left-8 md:left-1/2 -translate-x-1/2 flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-white border border-brand-gold-bright shadow-[0_8px_32px_-8px_rgba(200,169,106,0.4)]`}>
                    <Icon size={26} strokeWidth={1.4} className="text-brand-navy" />
                  </div>
                  {/* content */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${left ? "md:pr-24 md:text-right" : "md:pl-24"}`}>
                    <div className="text-[0.7rem] tracking-[0.3em] uppercase text-brand-gold-bright">Aşama 0{i + 1}</div>
                    <div className="mt-3 font-serif text-2xl md:text-4xl text-brand-navy leading-tight">
                      {pick({ tr: w.tr, en: w.en, ar: w.ar })}
                    </div>
                    <div className="mt-4 text-brand-medium leading-relaxed max-w-md md:inline-block">{pick(w.d)}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   3. WHY US — 8 premium cards
============================================================================ */
const WHY_CARDS = [
  { tr: "Arsa Tedariği", en: "Land Acquisition", ar: "شراء الأرض" },
  { tr: "Mimarlık", en: "Architecture", ar: "العمارة" },
  { tr: "Mühendislik", en: "Engineering", ar: "الهندسة" },
  { tr: "İnşaat", en: "Construction", ar: "البناء" },
  { tr: "Franchise Danışmanlığı", en: "Franchise Consulting", ar: "استشارات الامتياز" },
  { tr: "İşletme Yönetimi", en: "Operations Management", ar: "إدارة العمليات" },
  { tr: "Gayrimenkul Yatırım Danışmanlığı", en: "Real Estate Advisory", ar: "استشارات العقارات" },
  { tr: "Proje Geliştirme", en: "Project Development", ar: "تطوير المشاريع" },
];

const WhyChapter = () => {
  const { pick } = useI18n();
  return (
    <section className="bg-brand-navy-deep text-white relative overflow-hidden" data-testid="about-why">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="container-x py-24 md:py-36 relative">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-[0.7rem] tracking-[0.3em] uppercase text-brand-gold-bright">
              {pick({ tr: "Bölüm 03 — Uzmanlık", en: "Chapter 03 — Expertise", ar: "الفصل 03 — الخبرة" })}
            </div>
            <h2 className="font-serif mt-6 leading-[1.05]" style={{ fontSize: "clamp(2.5rem, 5vw, 5rem)" }}>
              {pick({ tr: "Neden Fırat Construction?", en: "Why Fırat Construction?", ar: "لماذا فرات؟" })}
            </h2>
          </div>
          <div className="text-white/60 max-w-sm">
            {pick({
              tr: "Sekiz disiplin. Tek bir yönetim. Uçtan uca sorumluluk.",
              en: "Eight disciplines. One management. End-to-end accountability.",
              ar: "ثمانية تخصصات. إدارة واحدة. مسؤولية شاملة.",
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {WHY_CARDS.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.19, 1, 0.22, 1] }}
              whileHover={{ y: -6 }}
              className="relative border border-white/10 bg-brand-navy/60 hover:bg-brand-primary-bright/20 hover:border-brand-gold-bright p-5 md:p-8 transition-colors duration-500 aspect-square flex flex-col justify-between group cursor-default"
              data-testid={`why-card-${i}`}
            >
              <div className="font-serif text-brand-gold-bright text-3xl md:text-5xl">0{i + 1}</div>
              <div>
                <div className="w-8 h-px bg-brand-gold-bright mb-3 group-hover:w-16 transition-all duration-500" />
                <div className="font-serif text-lg md:text-2xl leading-tight">{pick(c)}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   4. INTERNATIONAL HOTEL & RESIDENCE BRANDS — brand aggregator
============================================================================ */
const HOTEL_BRANDS = [
  "IHG Hotels & Resorts", "Marriott International", "Hilton Worldwide",
  "Wyndham Hotels & Resorts", "Accor", "Hyatt Hotels Corporation",
  "Radisson Hotel Group", "Minor Hotels", "Barceló Hotel Group",
  "Kempinski Hotels", "Mandarin Oriental", "Four Seasons Hotels & Resorts",
  "Six Senses", "Rixos Hotels", "Swissôtel",
  "Mövenpick Hotels & Resorts", "Fairmont Hotels & Resorts", "Pullman Hotels & Resorts",
  "DoubleTree by Hilton", "Holiday Inn", "Crowne Plaza",
  "voco Hotels", "InterContinental Hotels & Resorts",
];

const PROCESS_STEPS = [
  { tr: "Lokasyon Analizi", en: "Location Analysis", ar: "تحليل الموقع" },
  { tr: "Marka Uygunluk Değerlendirmesi", en: "Brand Fit Evaluation", ar: "تقييم ملاءمة العلامة" },
  { tr: "Franchise & Yönetim Görüşmeleri", en: "Franchise & Management Talks", ar: "محادثات الامتياز والإدارة" },
  { tr: "Proje Geliştirme ve Marka Lansmanı", en: "Project Development & Brand Launch", ar: "تطوير المشروع وإطلاق العلامة" },
];

const GarnerChapter = () => {
  const { pick } = useI18n();
  return (
    <section className="bg-white" data-testid="about-hotel-brands">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left — cinematic hotel/residence & city image */}
        <div className="lg:col-span-6 relative h-[60vh] lg:h-auto lg:min-h-screen overflow-hidden">
          <motion.img
            initial={{ scale: 1.15 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2, ease: [0.19, 1, 0.22, 1] }}
            src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1600&q=80"
            alt="Uluslararası Otel & Residence Markaları"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-deep/80 via-brand-navy/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-white" />
          <div className="absolute bottom-8 left-8 lg:top-8 text-white lg:text-brand-navy">
            <div className="text-[0.68rem] tracking-[0.32em] uppercase text-brand-primary-bright">
              {pick({ tr: "Bölüm 04 — Global Marka Geliştirme", en: "Chapter 04 — Global Brand Development", ar: "الفصل 04" })}
            </div>
          </div>
          <div className="absolute inset-x-6 bottom-6 lg:hidden text-white">
            <div className="font-serif text-2xl leading-tight">Global Otel & Residence</div>
          </div>
        </div>

        {/* Right — content + process timeline */}
        <div className="lg:col-span-6 flex items-start bg-brand-light">
          <div className="container-x py-16 md:py-24 w-full">
            <h2
              className="font-serif text-brand-navy leading-[1.05]"
              style={{ fontSize: "clamp(2rem, 4.2vw, 3.5rem)" }}
              data-testid="hotel-brands-title"
            >
              {pick({
                tr: "Uluslararası Otel & Residence Markaları",
                en: "International Hotel & Residence Brands",
                ar: "علامات فنادق وسكن دولية",
              })}
            </h2>

            <p className="mt-6 text-brand-dark leading-[1.75] max-w-xl">
              {pick({
                tr: "Fırat Construction & Real Estate olarak, dünyanın önde gelen otel ve residence markalarını Türkiye'deki doğru yatırım projeleriyle buluşturuyoruz.",
                en: "As Fırat Construction & Real Estate we connect the world's leading hotel and residence brands with the right investment projects in Türkiye.",
                ar: "نربط أكبر علامات الفنادق والسكن العالمية بأفضل المشاريع الاستثمارية في تركيا.",
              })}
            </p>
            <p className="mt-4 text-brand-medium leading-[1.75] max-w-xl">
              {pick({
                tr: "Arsa geliştirme, fizibilite, yatırım modeli, marka eşleştirmesi ve franchise süreçlerini tek çatı altında yöneterek uluslararası markaların Türkiye'deki yeni projelerine değer katıyoruz.",
                en: "We manage land development, feasibility, investment modeling, brand matchmaking, and franchise processes under one roof — adding value to every new project.",
                ar: "ندير كل عمليات التطوير والجدوى والنمذجة الاستثمارية والامتياز تحت سقف واحد.",
              })}
            </p>

            {/* Brand grid */}
            <div className="mt-10">
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-4">
                {pick({
                  tr: "Çalıştığımız Global Marka Grupları",
                  en: "Global brand groups we partner with",
                  ar: "مجموعات العلامات العالمية",
                })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="hotel-brands-list">
                {HOTEL_BRANDS.map((b, i) => (
                  <motion.div
                    key={b}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: Math.min(i, 20) * 0.02 }}
                    className="flex items-center gap-2.5 py-1.5 border-b border-brand-primary/10"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                    <span className="text-sm text-brand-dark leading-snug">{b}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Sub-message */}
            <blockquote className="mt-10 pl-5 border-l-2 border-brand-primary/60 font-serif italic text-brand-navy" style={{ fontSize: "clamp(1rem, 1.4vw, 1.2rem)", lineHeight: 1.55 }}>
              {pick({
                tr: "Uluslararası markaları doğru lokasyonlar ve güçlü yatırım projeleriyle buluşturarak Türkiye'de franchise ve marka geliştirme süreçlerini yönetiyoruz.",
                en: "We orchestrate franchise and brand development in Türkiye by pairing international brands with the right locations and robust investment projects.",
                ar: "ندير عمليات الامتياز وتطوير العلامة في تركيا بربط العلامات الدولية بالمواقع المناسبة.",
              })}
            </blockquote>

            {/* Process timeline */}
            <div className="mt-12 relative">
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-6">
                {pick({ tr: "Çalışma Sürecimiz", en: "Our Process", ar: "منهجيتنا" })}
              </div>
              <div className="absolute left-6 top-16 bottom-4 w-px bg-brand-primary/25" />
              <div className="space-y-8">
                {PROCESS_STEPS.map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="relative pl-16"
                    data-testid={`hotel-step-${i}`}
                  >
                    <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-white border border-brand-primary flex items-center justify-center font-serif text-brand-navy shadow-sm">
                      {i + 1}
                    </div>
                    <div className="font-serif text-xl md:text-2xl text-brand-navy leading-tight">{pick(s)}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   5. NUMBERS — animated counters
============================================================================ */
const useCount = (target, active, dur = 1600) => {
  const [val, setVal] = useState("0");
  useEffect(() => {
    if (!active) return;
    const m = String(target).match(/^([\d.,]+)([+\s\S]*)$/);
    if (!m) { setVal(String(target)); return; }
    const num = parseFloat(m[1].replace(",", "."));
    const suf = m[2] || "";
    const start = performance.now(); let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = Math.round(num * eased);
      setVal(`${cur}${suf}`);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, dur]);
  return val;
};

const NUMBERS = [
  { v: "1000+", t: { tr: "Çözüm Ortağı Firma", en: "Partner Firms", ar: "شركة شريكة" } },
  { v: "81", t: { tr: "İl — Türkiye Geneli", en: "Provinces — Nationwide", ar: "محافظة" } },
  { v: "25+", t: { tr: "Yatırım Zirvesi", en: "Investment Summits", ar: "قمة استثمارية" } },
  { v: "10000+", t: { tr: "Yatırımcı", en: "Investors", ar: "مستثمر" } },
];

const NumberItem = ({ n, i, active }) => {
  const { pick } = useI18n();
  const val = useCount(n.v, active);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay: i * 0.15, ease: [0.19, 1, 0.22, 1] }}
      className="border-t border-white/15 pt-8 md:pt-12"
      data-testid={`about-number-${i}`}
    >
      <div className="font-serif text-brand-gold-bright leading-[0.9]" style={{ fontSize: "clamp(3.5rem, 8vw, 8rem)" }}>
        {val}
      </div>
      <div className="mt-6 text-white/70 text-sm md:text-base max-w-xs leading-relaxed">{pick(n.t)}</div>
    </motion.div>
  );
};

const NumbersChapter = () => {
  const { pick } = useI18n();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  return (
    <section ref={ref} className="bg-brand-navy text-white relative overflow-hidden" data-testid="about-numbers">
      <div className="grain absolute inset-0 pointer-events-none" />
      <div className="container-x py-28 md:py-40 relative">
        <div className="max-w-3xl">
          <div className="text-[0.7rem] tracking-[0.3em] uppercase text-brand-gold-bright">
            {pick({ tr: "Bölüm 05 — Rakamlarla", en: "Chapter 05 — In Numbers", ar: "الفصل 05" })}
          </div>
          <h2 className="font-serif mt-8 leading-[1.05]" style={{ fontSize: "clamp(2.5rem, 5vw, 5rem)" }}>
            {pick({ tr: "Rakamlarla Fırat Construction", en: "Fırat Construction in Numbers", ar: "فرات بالأرقام" })}
          </h2>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {NUMBERS.map((n, i) => <NumberItem key={i} n={n} i={i} active={inView} />)}
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   6. SUMMITS — Türkiye map + events
============================================================================ */
const SUMMITS = [
  { city: "İstanbul", top: "34%", left: "42%" },
  { city: "Ankara", top: "45%", left: "56%" },
  { city: "İzmir", top: "56%", left: "30%" },
  { city: "Antalya", top: "78%", left: "48%" },
  { city: "Bursa", top: "38%", left: "38%" },
  { city: "Adana", top: "70%", left: "68%" },
  { city: "Trabzon", top: "22%", left: "78%" },
  { city: "Konya", top: "62%", left: "52%" },
];

const SummitsChapter = () => {
  const { pick } = useI18n();
  return (
    <section className="bg-brand-light" data-testid="about-summits">
      <div className="container-x py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-[0.7rem] tracking-[0.3em] uppercase text-brand-gold-bright">
              {pick({ tr: "Bölüm 06 — Sektör Liderliği", en: "Chapter 06 — Industry Leadership", ar: "الفصل 06" })}
            </div>
            <h2 className="font-serif mt-8 text-brand-navy leading-[1.05]" style={{ fontSize: "clamp(2.25rem, 4.6vw, 4.5rem)" }}>
              {pick({
                tr: "Türkiye'nin dört bir yanında Arsa Yatırım Zirveleri.",
                en: "Land Investment Summits across all of Türkiye.",
                ar: "قمم استثمارية عبر تركيا.",
              })}
            </h2>
            <p className="mt-6 text-brand-medium leading-relaxed max-w-md">
              {pick({
                tr: "25'ten fazla ilde binlerce yatırımcıyla bir araya geldik. Sektörün nabzını biz tutuyoruz.",
                en: "We convene with thousands of investors in 25+ provinces. We set the pulse of the industry.",
                ar: "نلتقي بآلاف المستثمرين.",
              })}
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              {[
                { k: "25+", v: { tr: "Şehir", en: "Cities", ar: "مدن" } },
                { k: "10K+", v: { tr: "Katılımcı", en: "Attendees", ar: "حضور" } },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-serif text-4xl md:text-5xl text-brand-navy">{s.k}</div>
                  <div className="mt-2 text-[0.66rem] tracking-[0.24em] uppercase text-brand-medium">{pick(s.v)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative aspect-[16/10] bg-white border border-black/5 overflow-hidden">
              {/* stylized Türkiye silhouette using dots */}
              <svg viewBox="0 0 100 55" className="absolute inset-0 w-full h-full" aria-hidden="true">
                {Array.from({ length: 15 }).map((_, r) =>
                  Array.from({ length: 30 }).map((__, c) => (
                    <circle key={`${r}-${c}`} cx={c * 3.3 + 2} cy={r * 3.6 + 2} r="0.4" fill="#2E3F7F" opacity="0.12" />
                  ))
                )}
              </svg>
              {SUMMITS.map((s, i) => (
                <motion.div
                  key={s.city}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  style={{ top: s.top, left: s.left }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 group"
                  data-testid={`summit-${s.city}`}
                >
                  <div className="relative w-3 h-3 bg-brand-gold-bright rounded-full">
                    <span className="absolute inset-0 rounded-full bg-brand-gold-bright animate-ping opacity-60" />
                  </div>
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[0.66rem] tracking-[0.22em] uppercase text-brand-navy font-medium opacity-0 group-hover:opacity-100 whitespace-nowrap bg-white/90 px-2 py-1 transition-opacity">
                    {s.city}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   FINAL — Full-screen closer
============================================================================ */
const FinalChapter = () => {
  const { pick } = useI18n();
  return (
    <section className="relative min-h-screen overflow-hidden bg-brand-navy text-white flex items-center" data-testid="about-final">
      <video autoPlay muted loop playsInline poster="https://images.unsplash.com/photo-1707584492157-909361644c6d" className="absolute inset-0 w-full h-full object-cover opacity-40">
        <source src="https://cdn.pixabay.com/video/2023/09/05/179066-861433553_large.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/60 via-brand-navy/70 to-brand-navy/90" />
      <div className="grain absolute inset-0 pointer-events-none" />

      <div className="relative z-10 container-x py-24 md:py-32">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} className="text-[0.7rem] tracking-[0.3em] uppercase text-brand-gold-bright">
          {pick({ tr: "Son Bölüm — Vizyon", en: "Final Chapter — Vision", ar: "الفصل الأخير" })}
        </motion.div>
        {[
          { tr: "Biz yalnızca bina inşa etmiyoruz.", en: "We don't just build buildings.", ar: "نحن لا نبني المباني فقط." },
          { tr: "Yatırımları planlıyor,", en: "We plan investments,", ar: "نخطط للاستثمارات،" },
          { tr: "projeleri geliştiriyor,", en: "develop projects,", ar: "نطور المشاريع،" },
          { tr: "markaları büyütüyor,", en: "grow brands,", ar: "ننمي العلامات،" },
          { tr: "işletmeleri yönetiyor,", en: "manage operations,", ar: "ندير العمليات،" },
          { tr: "ve geleceği inşa ediyoruz.", en: "and build the future.", ar: "ونبني المستقبل." },
        ].map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.15, ease: [0.19, 1, 0.22, 1] }}
            className={`font-serif mt-4 md:mt-6 leading-[1.05] ${i === 0 ? "text-white" : i === 5 ? "text-brand-gold-bright" : "text-white/85"}`}
            style={{ fontSize: "clamp(1.75rem, 4.4vw, 4.5rem)" }}
          >
            {pick(line)}
          </motion.div>
        ))}

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 1.2 }} className="mt-16">
          <Link to="/projects" className="inline-flex items-center gap-4 px-10 py-6 bg-brand-gold-bright text-brand-navy hover:bg-white hover:text-brand-navy transition-colors group" data-testid="about-final-cta">
            <span className="font-serif text-xl md:text-2xl">
              {pick({ tr: "Projelerimizi Keşfedin", en: "Explore Our Projects", ar: "اكتشف مشاريعنا" })}
            </span>
            <ArrowRight size={22} strokeWidth={1.5} className="group-hover:translate-x-2 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

/* ============================================================================
   ABOUT PAGE
============================================================================ */
const AboutPage = () => {
  const { settings } = useOutletContext() || {};
  const { pick } = useI18n();
  return (
    <div data-testid="about-page">
      <Seo fallbackTitle={pick({ tr: "Hakkımızda — Fırat Construction", en: "About — Fırat Construction", ar: "من نحن" })} />
      <HeroChapter />
      <WorkflowChapter />
      <WhyChapter />
      <GarnerChapter />
      <NumbersChapter />
      <SummitsChapter />
      <FinalChapter />
      <LeadCallout settings={settings} />
    </div>
  );
};

export default AboutPage;
